import React, { useEffect } from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { useDataSourcesByBbox } from "../../../api/dataSources";
import { DataSource } from "../../../types/dataSource";

type Bbox = { top: number; right: number; bottom: number; left: number };

/** Prefer the built-in (Mapbox Streets) roads source, else the first listed. */
const defaultRoadSource = (sources: DataSource[]): DataSource | undefined =>
  sources.find((s) => s.is_builtin) ?? sources[0];

/** Which `roads` data source is cut out of the vegetation when stamping
 *  roads. Pre-selects the built-in source (what every project used before
 *  roads became selectable) and re-selects it if the chosen id disappears
 *  from the list, so the wizard never sends a stale id. */
const RoadSourceSelect: React.FC<{
  value: string;
  bbox: Bbox;
  onChange: (value: string) => void;
}> = ({ value, bbox, onChange }) => {
  const { dataSources, isLoading } = useDataSourcesByBbox("roads", bbox);

  useEffect(() => {
    if (isLoading || dataSources.length === 0) return;
    if (value !== "" && dataSources.some((s) => String(s.id) === value)) return;
    const fallback = defaultRoadSource(dataSources);
    if (fallback) onChange(String(fallback.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, dataSources, value]);

  const selected = dataSources.find((s) => String(s.id) === value);
  const detail =
    selected === undefined
      ? null
      : selected.adapter_type === "mapbox_vector_tiles"
        ? "Corridor widths follow the road class (tracks 3 m up to motorways 30 m)."
        : `Lines are buffered to ${
            typeof selected.config?.line_width_m === "number"
              ? `${selected.config.line_width_m} m`
              : "the default 10 m"
          }${
            Array.isArray(selected.config?.filters) && (selected.config.filters as unknown[]).length > 0
              ? `, after ${(selected.config.filters as unknown[]).length} attribute filter(s)`
              : ""
          }.`;

  if (!isLoading && dataSources.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No roads data sources are configured, so nothing will be cut out. Add one under
        Roads in the Data Management console.
      </Typography>
    );
  }

  return (
    <Box>
      <FormControl fullWidth size="small">
        <InputLabel>Roads source</InputLabel>
        <Select
          value={dataSources.some((s) => String(s.id) === value) ? value : ""}
          label="Roads source"
          onChange={(e) => onChange(String(e.target.value))}
        >
          {dataSources.map((s) => (
            <MenuItem key={s.id} value={String(s.id)}>
              {s.name}
              {s.is_builtin ? " (built-in)" : ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {detail && (
        <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mt: 0.5 }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
};

export default RoadSourceSelect;
